import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RotateCw, Wand2, Bot, Star, TrendingUp, Megaphone, BarChart3, Loader2, Check, Clock, Share2 } from "lucide-react";
import { AI_BG_PRESET_KEY } from "@/components/AiBgPresetSelector";
import { runAiBgOnCarImages } from "@/lib/aiBgRun";
import { AiBgComparison } from "@/components/AiBgComparison";
import { OriginalSnapshotGallery } from "@/components/OriginalSnapshotGallery";
import { getAddonInfo } from "@/lib/creditAddons";
import { AddonInfoButton } from "@/components/AddonInfoButton";
import {
  normalizeAiSupportBrief,
  aiSupportBriefValid,
  aiSupportBriefToPayload,
} from "@/components/AiSupportBriefEditor";
import { normalize360Frames } from "@/lib/viewer360Frames";
import { isServiceAvailable, serviceUnavailableMessage } from "@/lib/serviceAvailability";
import { isAddonActive } from "@/lib/carBoost";

const REPEATABLE_ONE_TIME = [
  { key: "facebook_crosspost", Icon: Share2 },
];

const ONE_TIME = [
  { key: "viewer_360", Icon: RotateCw },
  { key: "ai_photo_bg", Icon: Wand2 },
  { key: "ai_customer_support", Icon: Bot },
];

const WEEKLY = [
  { key: "list_boost", Icon: Star },
  { key: "priority_ranking", Icon: TrendingUp },
  { key: "meta_ads", Icon: Megaphone },
];

export function AddonPanel({ car, config, onChange, onEditListing }) {
  const [busy, setBusy] = useState(null);
  const aibgPreset = AI_BG_PRESET_KEY;
  const [aibgRunning, setAibgRunning] = useState(false);
  const [aibgCompare, setAibgCompare] = useState(null); // {originals, processed, preset}
  const oneTimeCfg = config?.addons_one_time || {};
  const weeklyCfg = config?.addons_weekly || {};
  const dailyStats = config?.detailed_stats_daily ?? 5;
  const hasAiBg = !!car?.addon_ai_photo_bg;
  const hasAiSupport = !!car?.addon_ai_customer_support;
  const hasViewer360 = !!car?.addon_viewer_360;
  const frames360Count = normalize360Frames(car?.images_360).length;
  const serviceBlocked = (key, { allowIfActive = false, active = false } = {}) => {
    if (allowIfActive && active) return null;
    if (!isServiceAvailable(config, key)) return serviceUnavailableMessage(config, key);
    return null;
  };

  const addonIsOn = (type) => {
    if (type === "detailed_stats") return !!car?.detailed_stats;
    if (type === "facebook_crosspost") return false;
    if (type === "list_boost" || type === "priority_ranking" || type === "meta_ads") {
      return isAddonActive(car, type);
    }
    return !!car?.[`addon_${type}`];
  };

  const buy = async (type) => {
    const turningOff = addonIsOn(type);
    const blockMsg = serviceBlocked(type, {
      allowIfActive: type === "detailed_stats",
      active: !!car?.detailed_stats,
    });
    if (!turningOff && blockMsg) {
      toast.error(blockMsg);
      return;
    }
    if (type === "ai_customer_support" && !hasAiSupport && !turningOff) {
      const brief = normalizeAiSupportBrief(car?.ai_support_private);
      if (!aiSupportBriefValid(brief)) {
        if (onEditListing) {
          onEditListing({ focusAiSupport: true });
          toast.message("Az AI briefet a szerkesztésben töltsd ki — utána itt aktiválhatod.");
        } else {
          toast.message("Nyisd meg a hirdetés szerkesztését, töltsd ki az AI briefet, majd kapcsold be.");
        }
        return;
      }
    }
    setBusy(type);
    try {
      const body = { type, enable: !turningOff };
      if (type === "ai_customer_support" && !turningOff) {
        body.ai_support_private = aiSupportBriefToPayload(normalizeAiSupportBrief(car?.ai_support_private));
      }
      const r = await api.post(`/my/cars/${car.id}/addon`, body);
      const updatedCar = r.data.car;
      if (turningOff) {
        toast.success("Kiegészítő kikapcsolva");
        onChange?.(updatedCar);
        return;
      }
      if (type === "facebook_crosspost") {
        toast.success("Facebook megosztás kérése elküldve");
      } else {
        toast.success("Kiegészítő aktiválva");
      }
      onChange?.(updatedCar);

      if (type === "viewer_360") {
        toast.message("360° aktiválva — nyisd meg a Szerkesztést, ott veheted fel a videót.");
      }

      if (type === "ai_photo_bg") {
        const fromCar = updatedCar.images?.length
          ? updatedCar.images
          : [updatedCar.image].filter(Boolean);
        const fromProp = car?.images?.length ? car.images : [car?.image].filter(Boolean);
        const imgs = fromProp.length >= fromCar.length ? fromProp : fromCar;
        if (!imgs.length) {
          toast.message("AI háttér aktiválva — tölts fel képet, majd mentsd a hirdetést.");
          return;
        }
        setAibgRunning(true);
        toast.info(`AI háttércsere folyamatban… (${imgs.length} kép)`);
        try {
          const result = await runAiBgOnCarImages(updatedCar.id, imgs, aibgPreset);
          const next = {
            ...updatedCar,
            image: result.images?.[0] || updatedCar.image,
            images: result.images || updatedCar.images,
            aibg_last_preset: aibgPreset,
          };
          onChange?.(next);
          toast.success(`AI háttér kész — ${result.processed} kép`);
          setAibgCompare({ originals: result.originals, processed: result.images || [], preset: aibgPreset });
        } catch (e) {
          toast.error(e.response?.data?.detail || e.message || "AI háttércsere sikertelen");
        } finally {
          setAibgRunning(false);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sikertelen aktiválás");
    } finally {
      setBusy(null);
    }
  };

  const untilLabel = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (d < new Date()) return null;
    return d.toLocaleDateString("hu-HU");
  };

  return (
    <div data-testid={`addon-panel-${car.id}`} className="mt-3 pt-3 border-t border-white/5 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Kiegészítők</div>

      <div className="space-y-2">
        {REPEATABLE_ONE_TIME.map(({ key, Icon }) => {
          const info = getAddonInfo(key);
          const cost = oneTimeCfg[key];
          const unavail = serviceBlocked(key);
          return (
            <button
              key={key}
              data-testid={`addon-onetime-${key}-${car.id}`}
              disabled={busy === key || cost == null || !!unavail}
              onClick={() => buy(key)}
              className={`w-full flex flex-col items-stretch gap-1 py-2 px-3 rounded-lg text-xs transition-all border ${
                unavail
                  ? "opacity-60 cursor-not-allowed bg-white/[0.02] border-amber-500/20"
                  : "bg-white/[0.03] hover:bg-white/[0.06] border-white/5"
              }`}
            >
              <span className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 min-w-0">
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{info?.label}</span>
                <AddonInfoButton addonKey={key} testid={`addon-panel-info-${key}-${car.id}`} />
              </span>
              {busy === key ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : unavail ? (
                <span className="text-amber-300/90 text-[10px]">Szünetel</span>
              ) : (
                <span className="text-electric">{cost ?? "?"} kr / kérés</span>
              )}
              </span>
              {unavail && (
                <span className="text-[10px] text-amber-300/80 leading-snug pl-5">{unavail}</span>
              )}
            </button>
          );
        })}

        {ONE_TIME.map(({ key, Icon }) => {
          const info = getAddonInfo(key);
          const active = addonIsOn(key);
          const cost = oneTimeCfg[key];
          const unavail = active ? null : serviceBlocked(key);
          return (
            <button
              key={key}
              data-testid={`addon-onetime-${key}-${car.id}`}
              disabled={busy === key || cost == null || (!active && !!unavail)}
              onClick={() => buy(key)}
              title={active ? "Kattints a kikapcsoláshoz" : undefined}
              className={`w-full flex flex-col items-stretch gap-1 py-2 px-3 rounded-lg text-xs transition-all border ${
                active
                  ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30 hover:bg-emerald-500/15"
                  : unavail
                    ? "opacity-60 cursor-not-allowed bg-white/[0.02] border-amber-500/20"
                    : "bg-white/[0.03] hover:bg-white/[0.06] border-white/5"
              }`}
            >
              <span className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 min-w-0">
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{info?.label}</span>
                <AddonInfoButton addonKey={key} testid={`addon-panel-info-${key}-${car.id}`} />
              </span>
              {active ? (
                <span className="inline-flex items-center gap-1 text-emerald-300/90"><Check className="w-3 h-3" /> Kikapcsolás</span>
              ) : busy === key ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : unavail ? (
                <span className="text-amber-300/90 text-[10px]">Szünetel</span>
              ) : (
                <span className="text-electric">{cost ?? "?"} kr</span>
              )}
              </span>
              {unavail && (
                <span className="text-[10px] text-amber-300/80 leading-snug pl-5">{unavail}</span>
              )}
            </button>
          );
        })}

        {WEEKLY.map(({ key, Icon }) => {
          const info = getAddonInfo(key);
          const activeWeekly = addonIsOn(key);
          const until = untilLabel(car[`addon_${key}_until`]);
          const cost = weeklyCfg[key];
          const unavail = activeWeekly ? null : serviceBlocked(key);
          return (
            <button
              key={key}
              data-testid={`addon-weekly-${key}-${car.id}`}
              disabled={busy === key || cost == null || (!activeWeekly && !!unavail)}
              onClick={() => buy(key)}
              title={activeWeekly ? "Kattints a kikapcsoláshoz" : undefined}
              className={`w-full flex flex-col items-stretch gap-1 py-2 px-3 rounded-lg text-xs transition-all border ${
                unavail && !activeWeekly
                  ? "opacity-60 cursor-not-allowed bg-white/[0.02] border-amber-500/20"
                  : activeWeekly
                    ? "bg-amber-500/10 text-amber-200 border-amber-400/30 hover:bg-amber-500/15"
                    : "bg-white/[0.03] hover:bg-white/[0.06] border-white/5"
              }`}
            >
              <span className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 min-w-0">
                <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{info?.label}</span>
                <AddonInfoButton addonKey={key} testid={`addon-panel-info-${key}-${car.id}`} />
              </span>
              {busy === key ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : unavail && !activeWeekly ? (
                <span className="text-amber-300/90 text-[10px]">Szünetel</span>
              ) : activeWeekly ? (
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {until}-ig · Kikapcsolás</span>
              ) : (
                <span className="text-electric">{cost ?? "?"} kr / hét</span>
              )}
              </span>
              {unavail && (
                <span className="text-[10px] text-amber-300/80 leading-snug pl-5">{unavail}</span>
              )}
            </button>
          );
        })}

        {(() => {
          const statsUnavail = serviceBlocked("detailed_stats", {
            allowIfActive: true,
            active: !!car.detailed_stats,
          });
          return (
        <button
          data-testid={`addon-detailed-stats-${car.id}`}
          disabled={busy === "detailed_stats" || !!statsUnavail}
          onClick={() => buy("detailed_stats")}
          className={`w-full flex flex-col items-stretch gap-1 py-2 px-3 rounded-lg text-xs transition-all border ${
            car.detailed_stats
              ? "bg-electric/15 text-electric border-electric/30"
              : statsUnavail
                ? "opacity-60 cursor-not-allowed bg-white/[0.02] border-amber-500/20"
                : "bg-white/[0.03] hover:bg-white/[0.06] border-white/5"
          }`}
        >
          <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>{getAddonInfo("detailed_stats")?.label || "Részletes statisztika"}</span>
            <AddonInfoButton addonKey="detailed_stats" testid={`addon-panel-info-stats-${car.id}`} />
          </span>
          {busy === "detailed_stats" ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : car.detailed_stats ? (
            <span>Kikapcsolás · +{dailyStats} kr / nap</span>
          ) : statsUnavail ? (
            <span className="text-amber-300/90 text-[10px]">Szünetel</span>
          ) : (
            <span className="text-electric">+{dailyStats} kr / nap</span>
          )}
          </span>
          {statsUnavail && (
            <span className="text-[10px] text-amber-300/80 leading-snug pl-5">{statsUnavail}</span>
          )}
        </button>
          );
        })()}

        {hasAiBg && aibgRunning && (
          <div className="text-[11px] text-electric flex items-center gap-2 px-1" data-testid={`aibg-running-${car.id}`}>
            <Loader2 className="w-3 h-3 animate-spin" /> AI háttércsere…
          </div>
        )}

        {hasAiBg && onEditListing && (
          <p className="text-[11px] text-slate-400 px-1" data-testid={`aibg-edit-hint-${car.id}`}>
            AI háttér újrafuttatása:{" "}
            <button
              type="button"
              onClick={onEditListing}
              className="text-electric hover:text-electric-light underline underline-offset-2"
            >
              Szerkesztés megnyitása
            </button>
          </p>
        )}

        {hasAiBg && <OriginalSnapshotGallery car={car} onChange={onChange} />}

        {hasViewer360 && (
          <div
            className="mt-2 pt-2 border-t border-white/5 px-1"
            data-testid={`viewer-360-hint-${car.id}`}
          >
            <p className="text-[11px] text-slate-400 leading-relaxed flex gap-2">
              <RotateCw className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" strokeWidth={1.5} />
              <span>
                {frames360Count >= 12 ? (
                  <>
                    <span className="text-emerald-400/90">360° felvéve</span>
                    {" "}({frames360Count} kocka).{" "}
                  </>
                ) : (
                  <>
                    <span className="text-electric/90">360° aktiválva</span>
                    {" "}— a videót még nem mentetted.{" "}
                  </>
                )}
                A felvétel csak a hirdetés szerkesztésében érhető el
                {onEditListing ? (
                  <>
                    {" "}
                    (
                    <button
                      type="button"
                      data-testid={`viewer-360-open-edit-${car.id}`}
                      onClick={onEditListing}
                      className="text-electric hover:text-electric-light underline underline-offset-2"
                    >
                      Szerkesztés megnyitása
                    </button>
                    ).
                  </>
                ) : (
                  " (Szerkesztés gomb)."
                )}
              </span>
            </p>
          </div>
        )}

        {hasAiSupport && (
          <div
            className="mt-2 pt-2 border-t border-white/5 px-1"
            data-testid={`ai-support-edit-hint-${car.id}`}
          >
            <p className="text-[11px] text-slate-400 leading-relaxed flex gap-2">
              <Bot className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" strokeWidth={1.5} />
              <span>
                AI ügyfélszolgálat aktív — a privát brief csak a szerkesztésben módosítható
                {onEditListing ? (
                  <>
                    {" "}
                    (
                    <button
                      type="button"
                      data-testid={`ai-support-open-edit-${car.id}`}
                      onClick={() => onEditListing({ focusAiSupport: true })}
                      className="text-electric hover:text-electric-light underline underline-offset-2"
                    >
                      Szerkesztés megnyitása
                    </button>
                    ).
                  </>
                ) : (
                  "."
                )}
              </span>
            </p>
          </div>
        )}
      </div>
      <AiBgComparison
        open={!!aibgCompare}
        originals={aibgCompare?.originals || []}
        processed={aibgCompare?.processed || []}
        preset={aibgCompare?.preset}
        retrying={aibgRunning}
        onAccept={() => setAibgCompare(null)}
        onRevert={async () => {
          setAibgRunning(true);
          try {
            const r = await api.post(`/my/cars/${car.id}/ai-bg/revert`);
            onChange?.({ ...car, image: r.data.images?.[0], images: r.data.images });
            setAibgCompare(null);
            toast.success("Visszaállítva az eredeti képekre");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Visszaállítás sikertelen");
          } finally { setAibgRunning(false); }
        }}
      />
    </div>
  );
}
